import React from 'react';
import { Link } from 'react-router-dom';
import BLKLogo from '../components/BLKLogo';

const SUPPORT_EMAIL = 'admin@blkpixeltech.com';

const Section = ({ title, children }) => (
  <section className="mt-9">
    <h2 className="text-white text-xl font-bold tracking-tight">{title}</h2>
    <div className="text-gray-300 text-[14.5px] leading-relaxed mt-3 space-y-3">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2.5">
        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#a855f7] flex-shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div className="relative min-h-screen" data-testid="rapidreps-privacy-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#05050a]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[860px] mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="privacy-home-link">
            <BLKLogo />
          </Link>
          <Link
            to="/rapidreps-support"
            className="text-[13px] text-gray-300 hover:text-white transition-colors"
            data-testid="privacy-back-link"
          >
            ← Back to support
          </Link>
        </div>
      </header>

      <main className="max-w-[860px] mx-auto px-6 lg:px-8 py-14">
        <div className="text-[12px] font-semibold tracking-[0.3em] mb-4">
          <span className="bg-gradient-to-r from-[#a855f7] to-[#60a5fa] bg-clip-text text-transparent">
            RAPIDREPS
          </span>
        </div>
        <h1 className="text-white text-4xl sm:text-5xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="text-gray-500 text-[13px] mt-4">Last updated: June 26, 2026</p>

        <Section title="1. Who we are">
          <p>
            RapidReps is operated by BlkPixelTech ("RapidReps," "we," "us," or "our"), a Maryland-based
            business with offices at 10219 Windsor Oaks Way, Lanham, MD 20706. This Privacy Policy explains
            what data we collect when you use the RapidReps mobile app and related services, how we use it,
            who we share it with, and the choices you have. By using RapidReps you agree to this Policy. If
            you do not agree, please do not use the app.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>We collect the following categories of personal information:</p>
          <Bullets
            items={[
              <><strong className="text-white">Account information</strong> — name, email, phone number, password (hashed), date of birth, and role (trainee or trainer).</>,
              <><strong className="text-white">Profile content</strong> — profile photo, bio, training specialties, goals, certifications, pricing, highlight reel videos, vibe music selections.</>,
              <><strong className="text-white">Location data</strong> — precise GPS coordinates (foreground while the app is open, and "en-route" tracking during an active session if you grant background location permission) used to match nearby users, estimate arrival times, and display live session progress.</>,
              <><strong className="text-white">Payment information</strong> — billing details handled by our payment processor (Stripe). We never see or store full card numbers; we receive only a tokenized reference and transaction metadata. For trainer payouts, we collect the off-platform payout handle you choose to share (Zelle, PayPal, Venmo, or CashApp identifier) so our admin team can send your earnings.</>,
              <><strong className="text-white">Session and booking data</strong> — sessions you request, accept, complete, cancel; negotiation messages; reviews; tips; and timestamps.</>,
              <><strong className="text-white">Communications</strong> — in-app messages between trainees and trainers, support tickets, push notification responses.</>,
              <><strong className="text-white">Device & usage data</strong> — device model, OS version, app version, IP address, crash logs, analytics events, and feature interactions used to improve reliability and performance.</>,
            ]}
          />
        </Section>

        <Section title="3. How we use your information">
          <p>We use personal information to:</p>
          <Bullets
            items={[
              'Create and secure your account, authenticate sign-ins, and prevent fraud.',
              'Match trainees with nearby trainers and display profiles, pricing, and availability.',
              'Process payments through Stripe and arrange off-platform trainer payouts.',
              'Provide live session tracking, ETAs, and routing via Google Maps and Places APIs.',
              'Send transactional push notifications (booking confirmations, payment requests, session reminders) and, where permitted, occasional product updates.',
              'Respond to support requests and resolve disputes between trainees and trainers.',
              'Comply with legal obligations, enforce our Terms of Service, and protect the rights, safety, and property of RapidReps, users, and the public.',
            ]}
          />
        </Section>

        <Section title="4. How we share information">
          <p>We share personal information only as follows:</p>
          <Bullets
            items={[
              <><strong className="text-white">Between trainees and trainers</strong> — limited profile fields, session details, and in-app messages necessary to coordinate a booking.</>,
              <><strong className="text-white">With service providers</strong> — Stripe (payment processing), Apple Push Notification Service and Firebase Cloud Messaging (push notifications), Google Maps Platform (maps, routing, places autocomplete), Sentry (crash reporting), SendGrid (transactional email), and our cloud hosting providers. These providers process data on our behalf under contractual confidentiality obligations.</>,
              <><strong className="text-white">With our admin team</strong> — to manually arrange off-platform trainer payouts (Zelle, PayPal, Venmo, CashApp).</>,
              <><strong className="text-white">For legal reasons</strong> — to comply with subpoenas, court orders, or other legal process; to enforce our Terms; to protect users from imminent harm; or in connection with a merger, acquisition, or sale of all or part of our business.</>,
            ]}
          />
          <p>
            We do <strong className="text-white">not</strong> sell or rent your personal information to third
            parties for their own marketing.
          </p>
        </Section>

        <Section title="5. Location data">
          <p>
            RapidReps requests "While Using the App" location permission to surface nearby trainers and
            estimate arrival times. If you grant "Always" (background) permission, we additionally track
            location during an active session that is in "en-route" status so the other party can see your
            live progress. Background tracking automatically stops when the session ends. You can revoke
            location access at any time in your device Settings; some features (matching, ETAs, live
            tracking) will not work without it. We retain precise location only for the duration of an
            active session, then store coarse or aggregated location metadata for analytics.
          </p>
        </Section>

        <Section title="6. Payments and off-platform payouts">
          <p>
            All trainee payments are processed by Stripe, Inc. Your card details are entered directly into
            Stripe's secure SDK and never touch our servers. We receive a tokenized customer reference, a
            transaction ID, and a payment status.
          </p>
          <p>
            Trainer earnings are paid out manually by our admin team to the off-platform handle you choose
            (Zelle, PayPal, Venmo, or CashApp). To receive payouts you must provide an accurate handle in
            your profile. By providing a payout handle you authorize us to share it with the relevant
            payout-network counterparty to complete the transfer. We retain payout records for tax and audit
            purposes for at least 7 years.
          </p>
        </Section>

        <Section title="7. Push notifications & deep links">
          <p>
            With your permission we send push notifications for events such as booking requests, payment
            requests (one-tap deep links to the in-app payment screen), session reminders, and messages. You
            can disable push notifications at any time in your device Settings or in the RapidReps
            notification preferences screen. Disabling notifications will not stop critical transactional
            emails sent via SendGrid.
          </p>
        </Section>

        <Section title="8. Data retention">
          <p>
            We retain account and session data for as long as your account is active and for a reasonable
            period afterward to satisfy legal, accounting, and dispute-resolution obligations (typically up
            to 7 years for financial records). You can request earlier deletion using the in-app "Delete
            account" control or by emailing{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#c084fc] hover:underline">{SUPPORT_EMAIL}</a>.
            We will honor verifiable deletion requests except where retention is required by law.
          </p>
        </Section>

        <Section title="9. Your privacy rights">
          <p>Depending on where you live, you may have the right to:</p>
          <Bullets
            items={[
              <><strong className="text-white">Access</strong> — request a copy of the personal information we hold about you.</>,
              <><strong className="text-white">Correct</strong> — ask us to update inaccurate information.</>,
              <><strong className="text-white">Delete</strong> — ask us to delete your personal information, subject to legal retention limits.</>,
              <><strong className="text-white">Port</strong> — receive a machine-readable copy of certain data.</>,
              <><strong className="text-white">Opt-out</strong> — opt out of any "sale" or "sharing" of personal information for cross-context behavioral advertising. RapidReps does not sell or share personal information for advertising.</>,
            ]}
          />
          <p>
            California residents have specific rights under the CCPA/CPRA, including the right to
            non-discrimination for exercising those rights. To exercise any right, email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#c084fc] hover:underline">{SUPPORT_EMAIL}</a>{' '}
            from the address on file with us. We will verify your identity before fulfilling the request.
          </p>
        </Section>

        <Section title="10. Children">
          <p>
            RapidReps is intended for users 18 years of age or older. We do not knowingly collect personal
            information from anyone under 18. If you believe a child has provided us information, please
            contact{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#c084fc] hover:underline">{SUPPORT_EMAIL}</a>{' '}
            and we will delete it.
          </p>
        </Section>

        <Section title="11. Security">
          <p>
            We use industry-standard safeguards including TLS in transit, encrypted password hashing
            (bcrypt), Stripe-tokenized payments, signed JWT sessions, role-based access controls on our
            backend, and crash monitoring via Sentry. No internet transmission or electronic storage is 100%
            secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="12. International transfers">
          <p>
            RapidReps is operated from the United States. If you access the app from outside the U.S., your
            information will be transferred to and processed in the U.S. by us and our service providers
            under safeguards consistent with applicable law.
          </p>
        </Section>

        <Section title="13. Changes to this Policy">
          <p>
            We may update this Privacy Policy from time to time. We will revise the "Last updated" date at
            the top and, for material changes, give in-app or email notice. Continued use after a change
            constitutes acceptance.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>Questions, requests, or complaints about this Policy?</p>
          <p className="leading-relaxed">
            BlkPixelTech
            <br />
            10219 Windsor Oaks Way, Lanham, MD 20706
            <br />
            Email:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#c084fc] hover:underline">{SUPPORT_EMAIL}</a>
          </p>
        </Section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-[860px] mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-gray-500 text-[12px]">
            © {new Date().getFullYear()} BLKPixel Technologies, LLC. All rights reserved.
          </div>
          <div className="flex items-center gap-5 text-[12px]">
            <Link to="/rapidreps-support" className="text-gray-400 hover:text-white">Support</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-400 hover:text-white">{SUPPORT_EMAIL}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
